import { useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
  Button,
  Input,
  Switch,
} from "@chakra-ui/react";
import { formatTableValue } from "./formatTable";
import toast from "react-hot-toast";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { LevelCalculatorRateK } from "../../utils/contants";
import { saleOpeningEventMechanismParametersCall } from "../../api/saleOpeningEventMechanismParameters";
import { IoArrowBackOutline } from "react-icons/io5";
import {
  InputControl,
  TextareaControl,
} from "../../components/form/formControls";

const schemaAdd = Yup.object().shape({
  saleOpeningEventId: Yup.string().required("mã dự án là bắt buộc"),
  kRate: Yup.number()
    .transform((value, originalValue) => {
      if (typeof originalValue === "string" && originalValue.trim() === "") {
        return null;
      }
      return Number(originalValue);
    })
    .typeError("Hệ số k phải là một số hợp lệ")
    .min(0, "Hệ số k phải lớn hơn hoặc bằng 0")
    .max(100, "Hệ số k phải phải nhỏ hơn hoặc bằng 100")
    .required("Vui lòng nhập hệ số k"),
  transactionRate: Yup.number()
    .transform((value, originalValue) => {
      if (typeof originalValue === "string" && originalValue.trim() === "") {
        return null;
      }
      return Number(originalValue);
    })
    .typeError("Tỉ lệ % phí dịch vụ nhận về phải là một số hợp lệ")
    .min(0, "Tỉ lệ % phí dịch vụ nhận về phải lớn hơn hoặc bằng 0")
    .max(100, "Tỉ lệ % phí dịch vụ nhận về phải nhỏ hơn hoặc bằng 100")
    .required("Vui lòng nhập tỉ lệ % phí dịch vụ nhận về"),
  rateTN: Yup.number()
    .transform((value, originalValue) => {
      if (typeof originalValue === "string" && originalValue.trim() === "") {
        return null;
      }
      return Number(originalValue);
    })
    .typeError("Tỉ lệ TN MSH (đi kèm hệ số K) phải là một số hợp lệ")
    .min(0, "Tỉ lệ TN MSH (đi kèm hệ số K) phải lớn hơn hoặc bằng 0")
    .max(100, "Tỉ lệ TN MSH (đi kèm hệ số K) phải nhỏ hơn hoặc bằng 100")
    .required("Vui lòng nhập tỉ lệ TN MSH (đi kèm hệ số K)"),
});

const hasValue = (value) => value !== undefined && value !== null;

const getSafeNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isNaN(num) ? fallback : num;
};

const getDefaultLevelCalculatorRateK = () =>
  (LevelCalculatorRateK || []).map((item) => ({ ...item }));

const mapMechanismRows = (rows = []) =>
  rows.map((item) => ({
    id: item?.id || item?._id,
    name: item?.name,
    code: item?.code,
    mechanismRate: item?.mechanismRate,
    rate: item?.rate,
    mechanismRateElite: item?.mechanismRateElite,
    rateElite: item?.rateElite,
  }));

const TableUpdateProjectMechanism = ({
  saleOpeningEventId,
  onClose,
  id,
  onToggle,
  refetch,
  name,
}) => {
  const [levelCalculatorRateK, setLevelCalculatorRateK] = useState([]);
  const [kRate, setKRate] = useState(0);
  const [rate, setRate] = useState(0);
  const [rateDefault, setRateDefault] = useState(0);
  const [rateElite, setRateElite] = useState(0);
  const [rateEliteDefault, setRateEliteDefault] = useState(0);
  const [rateTN, setRateTN] = useState(0);
  const [ratePDVAndTNCVKDElite, setRatePDVAndTNCVKDElite] = useState(0);
  const [transactionRate, setTransactionRate] = useState(0);

  const [checkedSwitch, setCheckedSwitch] = useState(true);
  const [isAutoCalculate, setIsAutoCalculate] = useState(false);

  const skipInitialAutoRecalcRef = useRef(true);
  const previousAutoCalculateRef = useRef(true);

  const {
    watch,
    setValue,
    getValues,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schemaAdd),
    defaultValues: {
      saleOpeningEventId,
    },
  });

  const watchedRateEliteDefault = watch("rateEliteDefault");
  const watchedRateDefault = watch("rateDefault");

  const roundTo0 = (num) => Math.round(num);
  const roundTo1 = (num) => Math.round(num * 10) / 10;
  const roundTo2 = (num) => Math.round(num * 100) / 100;
  const roundTo4 = (num) => Math.round(num * 10000) / 10000;

  const resetAutoRecalcGuard = () => {
    skipInitialAutoRecalcRef.current = true;
  };

  const applyTopLevelValues = (data) => {
    if (hasValue(data?.kRate)) {
      setKRate(data.kRate);
      setValue("kRate", data.kRate);
    }

    if (hasValue(data?.rate)) {
      setRate(data.rate);
      setValue("rate", data.rate);
    }

    if (hasValue(data?.rateDefault)) {
      setRateDefault(data.rateDefault);
      setValue("rateDefault", data.rateDefault);
    }

    if (hasValue(data?.rateElite)) {
      setRateElite(data.rateElite);
      setValue("rateElite", data.rateElite);
    }

    if (hasValue(data?.rateEliteDefault)) {
      setRateEliteDefault(data.rateEliteDefault);
      setValue("rateEliteDefault", data.rateEliteDefault);
    }

    if (hasValue(data?.rateTN)) {
      setRateTN(data.rateTN);
      setValue("rateTN", data.rateTN);
    }

    if (hasValue(data?.ratePDVAndTNCVKDElite)) {
      setRatePDVAndTNCVKDElite(data.ratePDVAndTNCVKDElite);
    }

    if (hasValue(data?.transactionRate)) {
      setTransactionRate(data.transactionRate);
      setValue("transactionRate", data.transactionRate);
    }

    setValue("note", data?.note || "");
  };

  const fetchProjectMechanismParametersByProject = async (mechanismId) => {
    try {
      resetAutoRecalcGuard();

      let data;
      if (mechanismId) {
        data =
          await saleOpeningEventMechanismParametersCall.getById(mechanismId);
      }

      if (data) {
        if (data?.data) {
          setLevelCalculatorRateK(mapMechanismRows(data.data));
        } else {
          setLevelCalculatorRateK(getDefaultLevelCalculatorRateK());
        }

        applyTopLevelValues(data);

        if (data?.statusKRate === 1) {
          setCheckedSwitch(true);
          setValue("statusKRate", 1);
        } else {
          setCheckedSwitch(false);
          setValue("statusKRate", 2);
        }
      } else {
        setLevelCalculatorRateK(getDefaultLevelCalculatorRateK());

        setTransactionRate(100);
        setValue("transactionRate", 100);

        setRate(1.5);
        setValue("rate", 1.5);

        setRateDefault(1.5);
        setValue("rateDefault", 1.5);

        setRateElite(0.2);
        setValue("rateElite", 0.2);

        setRateEliteDefault(0.2);
        setValue("rateEliteDefault", 0.2);

        setCheckedSwitch(true);
        setValue("statusKRate", 1);
      }
    } catch (error) {
      console.error("Error", error);
      toast.error("Có lỗi xảy ra khi lấy ra dự án");
    }
  };

  const getProjectMechanismParametersByProject = async (mechanismId) => {
    try {
      resetAutoRecalcGuard();

      let data;
      if (mechanismId) {
        data =
          await saleOpeningEventMechanismParametersCall.getById(mechanismId);
      }

      if (data && data?.statusKRate === 1) {
        if (data?.data) {
          setLevelCalculatorRateK(mapMechanismRows(data.data));
        } else {
          setLevelCalculatorRateK(getDefaultLevelCalculatorRateK());
        }

        applyTopLevelValues(data);
      } else {
        setLevelCalculatorRateK(getDefaultLevelCalculatorRateK());
      }
    } catch (error) {
      console.error("Error", error);
      toast.error("Có lỗi xảy ra khi lấy ra dự án");
    }
  };

  useEffect(() => {
    if (saleOpeningEventId) {
      setValue("saleOpeningEventId", saleOpeningEventId);
      fetchProjectMechanismParametersByProject(id);
    } else {
      setLevelCalculatorRateK(getDefaultLevelCalculatorRateK());
    }
  }, [id, saleOpeningEventId, setValue]);

  useEffect(() => {
    if (checkedSwitch) {
      if (saleOpeningEventId) {
        getProjectMechanismParametersByProject(id);
      } else {
        setLevelCalculatorRateK(getDefaultLevelCalculatorRateK());
      }
    }
  }, [checkedSwitch, id, saleOpeningEventId]);

  useEffect(() => {
    if (!checkedSwitch || !isAutoCalculate) return;

    if (skipInitialAutoRecalcRef.current) {
      skipInitialAutoRecalcRef.current = false;
      return;
    }

    if (hasValue(watchedRateEliteDefault) || hasValue(watchedRateDefault)) {
      handleChangeInput({ id: null, key: null, value: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedRateEliteDefault,
    watchedRateDefault,
    checkedSwitch,
    isAutoCalculate,
  ]);

  useEffect(() => {
    if (
      previousAutoCalculateRef.current === false &&
      isAutoCalculate &&
      checkedSwitch &&
      levelCalculatorRateK?.length
    ) {
      handleChangeInput({ id: null, key: null, value: null });
    }

    previousAutoCalculateRef.current = isAutoCalculate;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAutoCalculate, checkedSwitch, levelCalculatorRateK]);

  const tableData = {
    headers: [
      {
        label: "Các cấp",
        key: "name",
      },
      {
        label: "Mã",
        key: "code",
      },
      {
        label: "Cơ chế % (CVKD)",
        key: "mechanismRate",
      },
      {
        label: "Tỉ lệ % (CVKD)",
        key: "rate",
      },
      {
        label: "Cơ chế % (CVKD Elite)",
        key: "mechanismRateElite",
      },
      {
        label: "Tỉ lệ % (CVKD Elite)",
        key: "rateElite",
      },
    ],
    data: levelCalculatorRateK,
  };

  const handleChangeInput = ({ id = null, key = null, value = null }) => {
    if (!levelCalculatorRateK?.length) return;

    if (!isAutoCalculate) {
      const updatedData = levelCalculatorRateK.map((item) =>
        item.id === id
          ? { ...item, [key]: value === "" ? "" : Number(value) }
          : item,
      );

      setLevelCalculatorRateK(updatedData);
      return;
    }

    let nextValue = value;
    if (nextValue < 0) nextValue = 0;
    if (nextValue > 100) nextValue = 100;

    let updatedData = levelCalculatorRateK.map((item) =>
      item.id === id ? { ...item, [key]: Number(nextValue) } : { ...item },
    );

    const CHUNG = updatedData.find((item) => item.code === "CHUNG");
    const CVKD = updatedData.find((item) => item.code === "CVKD");
    const TOTAL = updatedData.find((item) => item.code === "TOTAL");
    const CVKD_ELITE = updatedData.find((item) => item.code === "CVKD_ELITE");
    const REMAIN = updatedData.find((item) => item.code === "REMAIN");
    const GDK = updatedData.find((item) => item.code === "GDK");

    if (
      id === null &&
      (!CHUNG ||
        CHUNG?.mechanismRateElite === 0 ||
        !CVKD ||
        CVKD?.mechanismRateElite === 0)
    ) {
      const nextRate = rateDefault || 1.5;
      const nextRateElite = rateEliteDefault || 0.2;

      setRate(nextRate);
      setValue("rate", nextRate);
      setRateElite(nextRateElite);
      setValue("rateElite", nextRateElite);
      return;
    }

    const mechanismRateEliteCVKD = parseFloat(CVKD?.mechanismRateElite) || 0;
    const rateCVKD = parseFloat(CVKD?.rate) || 0;
    const rateTOTAL = parseFloat(TOTAL?.rate) || 0;
    const rateCVKD_ELITE = parseFloat(CVKD_ELITE?.rate) || 0;
    const mechanismRateEliteCHUNG =
      (parseFloat(CHUNG?.mechanismRateElite) || 0) / 1.1;

    updatedData = updatedData.map((item) => {
      if (item.code === "CVKD") {
        return {
          ...item,
          rateElite: roundTo1(
            (mechanismRateEliteCVKD * (CHUNG?.rateElite || 0)) /
              (mechanismRateEliteCHUNG || 1),
          ),
        };
      }
      return { ...item };
    });

    const updatedCVKD = updatedData.find((item) => item.code === "CVKD");
    const rateEliteCVKD = parseFloat(updatedCVKD?.rateElite || 0) || 0;

    let totalMechanismRate = 0;
    let totalRate = 0;
    let totalMechanismRateElite = 0;
    let totalRateElite = 0;
    let calculatedMechanismRateEliteGDK = 0;

    updatedData = updatedData.map((item) => {
      if (
        !["CVKD", "CHUNG", "TOTAL", "REMAIN", "CVKD_ELITE"].includes(item.code)
      ) {
        const dtRate = parseFloat(item?.rate) || 0;

        const calculatedRateElite =
          (dtRate * (rateTOTAL - rateEliteCVKD)) /
          (rateTOTAL - rateCVKD - rateCVKD_ELITE || 1);

        const calculatedMechanismRateElite =
          (calculatedRateElite * mechanismRateEliteCVKD) / (rateEliteCVKD || 1);

        if (item.code === "GDK") {
          calculatedMechanismRateEliteGDK = calculatedMechanismRateElite;
        }

        item = {
          ...item,
          rateElite:
            roundTo1(calculatedRateElite) >= 0
              ? roundTo1(calculatedRateElite)
              : 0,
          mechanismRateElite:
            roundTo2(calculatedMechanismRateElite) >= 0
              ? roundTo2(calculatedMechanismRateElite)
              : 0,
        };
      }

      if (!["CHUNG", "TOTAL", "REMAIN"].includes(item.code)) {
        totalMechanismRate += Number(item.mechanismRate || 0);
        totalRate += Number(item.rate || 0);
        totalMechanismRateElite += Number(item.mechanismRateElite || 0);
        totalRateElite += Number(item.rateElite || 0);
      }

      return item;
    });

    updatedData = updatedData.map((item) => {
      if (item.code === "TOTAL") {
        return {
          ...item,
          mechanismRate:
            roundTo1(totalMechanismRate || 0) >= 0
              ? roundTo1(totalMechanismRate || 0)
              : 0,
          rate: roundTo1(totalRate || 0) >= 0 ? roundTo1(totalRate || 0) : 0,
          mechanismRateElite:
            roundTo1(totalMechanismRateElite || 0) >= 0
              ? roundTo1(totalMechanismRateElite || 0)
              : 0,
          rateElite:
            roundTo0(totalRateElite || 0) >= 0
              ? roundTo0(totalRateElite || 0)
              : 0,
        };
      }

      if (item.code === "REMAIN") {
        const totalRow = updatedData.find((row) => row.code === "TOTAL");
        return {
          ...item,
          mechanismRate:
            roundTo1(
              (CHUNG?.mechanismRate || 0) - (totalRow?.mechanismRate || 0),
            ) >= 0
              ? roundTo1(
                  (CHUNG?.mechanismRate || 0) - (totalRow?.mechanismRate || 0),
                )
              : 0,
          rate:
            roundTo1((CHUNG?.rate || 0) - (totalRow?.rate || 0)) >= 0
              ? roundTo1((CHUNG?.rate || 0) - (totalRow?.rate || 0))
              : 0,
          mechanismRateElite:
            roundTo1((mechanismRateEliteCHUNG || 0) * 0.3) >= 0
              ? roundTo1((mechanismRateEliteCHUNG || 0) * 0.3)
              : 0,
          rateElite:
            roundTo1((CHUNG?.rateElite || 0) * 0.3) >= 0
              ? roundTo1((CHUNG?.rateElite || 0) * 0.3)
              : 0,
        };
      }

      return item;
    });

    const latestGDK = updatedData.find((item) => item.code === "GDK");

    const kRateData =
      roundTo4(
        (calculatedMechanismRateEliteGDK || 0) /
          (latestGDK?.mechanismRate || 1),
      ) >= 0
        ? roundTo4(
            (calculatedMechanismRateEliteGDK || 0) /
              (latestGDK?.mechanismRate || 1),
          )
        : 0;

    const rateData =
      roundTo4(
        ((calculatedMechanismRateEliteGDK || 0) /
          (latestGDK?.mechanismRate || 1)) *
          (rateDefault || 1.5),
      ) >= 0
        ? roundTo4(
            ((calculatedMechanismRateEliteGDK || 0) /
              (latestGDK?.mechanismRate || 1)) *
              (rateDefault || 1.5),
          )
        : 0;

    const rateEliteData =
      roundTo4(
        ((calculatedMechanismRateEliteGDK || 0) /
          (latestGDK?.mechanismRate || 1)) *
          (rateEliteDefault || 0.2),
      ) >= 0
        ? roundTo4(
            ((calculatedMechanismRateEliteGDK || 0) /
              (latestGDK?.mechanismRate || 1)) *
              (rateEliteDefault || 0.2),
          )
        : 0;

    const nextRateTN =
      roundTo4(
        mechanismRateEliteCVKD -
          ((calculatedMechanismRateEliteGDK || 0) /
            (latestGDK?.mechanismRate || 1)) *
            (rateDefault || 1.5) -
          ((calculatedMechanismRateEliteGDK || 0) /
            (latestGDK?.mechanismRate || 1)) *
            (rateEliteDefault || 0.2),
      ) >= 0
        ? roundTo4(
            mechanismRateEliteCVKD -
              ((calculatedMechanismRateEliteGDK || 0) /
                (latestGDK?.mechanismRate || 1)) *
                (rateDefault || 1.5) -
              ((calculatedMechanismRateEliteGDK || 0) /
                (latestGDK?.mechanismRate || 1)) *
                (rateEliteDefault || 0.2),
          )
        : 0;

    const rateTNNum =
      mechanismRateEliteCVKD -
      ((calculatedMechanismRateEliteGDK || 0) /
        (latestGDK?.mechanismRate || 1)) *
        (rateDefault || 1.5) -
      ((calculatedMechanismRateEliteGDK || 0) /
        (latestGDK?.mechanismRate || 1)) *
        (rateEliteDefault || 0.2);

    const rateDataNum =
      ((calculatedMechanismRateEliteGDK || 0) /
        (latestGDK?.mechanismRate || 1)) *
      (rateDefault || 1.5);

    const rateEliteDataNum =
      ((calculatedMechanismRateEliteGDK || 0) /
        (latestGDK?.mechanismRate || 1)) *
      (rateEliteDefault || 0.2);

    const ratePDVAndTNCVKDEliteData =
      roundTo4(rateTNNum + rateDataNum + rateEliteDataNum) >= 0
        ? roundTo4(rateTNNum + rateDataNum + rateEliteDataNum)
        : 0;

    setKRate(kRateData);
    setValue("kRate", kRateData);

    setRate(rateData);
    setValue("rate", rateData);

    setRateElite(rateEliteData);
    setValue("rateElite", rateEliteData);

    setRateTN(nextRateTN);
    setValue("rateTN", nextRateTN);

    setRatePDVAndTNCVKDElite(ratePDVAndTNCVKDEliteData);

    setLevelCalculatorRateK(updatedData);
  };

  const buildPayload = (formData) => {
    const statusKRateValue = Number(
      formData?.statusKRate ?? (checkedSwitch ? 1 : 2),
    );

    const isMechanismMode = statusKRateValue === 1;
    const shouldUseAutoCalculatedValues = isMechanismMode && isAutoCalculate;

    const payload = {
      kRate: shouldUseAutoCalculatedValues
        ? kRate
        : getSafeNumber(getValues("kRate"), kRate),
      saleOpeningEventId: formData?.saleOpeningEventId,
      statusKRate: statusKRateValue,
      transactionRate: shouldUseAutoCalculatedValues
        ? transactionRate
        : getSafeNumber(getValues("transactionRate"), transactionRate),
      rateElite: shouldUseAutoCalculatedValues
        ? rateElite
        : getSafeNumber(getValues("rateElite"), rateElite),
      rateEliteDefault: shouldUseAutoCalculatedValues
        ? rateEliteDefault
        : getSafeNumber(getValues("rateEliteDefault"), rateEliteDefault),
      rate: shouldUseAutoCalculatedValues
        ? rate
        : getSafeNumber(getValues("rate"), rate),
      rateDefault: shouldUseAutoCalculatedValues
        ? rateDefault
        : getSafeNumber(getValues("rateDefault"), rateDefault),
      rateTN: shouldUseAutoCalculatedValues
        ? rateTN
        : getSafeNumber(getValues("rateTN"), rateTN),
      note: formData?.note || "",
    };

    if (isMechanismMode) {
      payload.data = levelCalculatorRateK;
      payload.ratePDVAndTNCVKDElite = ratePDVAndTNCVKDElite;
      payload.saleOpeningEventId = saleOpeningEventId;
    }

    return payload;
  };

  const submitHandlerCreate = async (data) => {
    const toastCreate = toast.loading("Đang thêm tính hệ số K ...");
    try {
      const payload = buildPayload(data);

      await saleOpeningEventMechanismParametersCall.create(payload);
      await refetch();
      onToggle();
      onClose();
      toast.success("Thêm tính hệ số K thành công");
    } catch (error) {
      console.log({ error });
      toast.dismiss(toastCreate);
    }
    toast.dismiss(toastCreate);
  };

  const submitHandlerEdit = async (data) => {
    const toastCreate = toast.loading("Đang sửa tính hệ số K ...");
    try {
      const payload = buildPayload(data);

      await saleOpeningEventMechanismParametersCall.update(id, payload);

      await refetch();
      onToggle();
      onClose();
      toast.success("Sửa tính hệ số K thành công");
    } catch (error) {
      console.log({ error });
      toast.dismiss(toastCreate);
    }
    toast.dismiss(toastCreate);
  };

  return (
    <>
      <Box w={"full"}>
        <Flex
          onClick={onClose}
          cursor={"pointer"}
          gap={"8px"}
          alignItems={"center"}
          mb={"16px"}
          maxW={"max-content"}
        >
          <IoArrowBackOutline fontSize={"24px"} />
          <Text
            fontSize={"20px"}
            lineHeight={"19px"}
            fontWeight={"600"}
            color={"var(--color-header-employee)"}
          >
            Quay lại
          </Text>
        </Flex>

        <form
          onSubmit={handleSubmit((data) => {
            if (id) {
              submitHandlerEdit(data);
            } else {
              submitHandlerCreate(data);
            }
          })}
        >
          <Flex flexDirection={"column"} gap={{ base: "16px" }} mb={"20px"}>
            <Flex
              backgroundColor="var(--color-main)"
              borderRadius={{ base: "12px" }}
              flexDirection={"column"}
            >
              <Box p={"16px 16px 12px 16px"}>
                <Text
                  fontSize={"20px"}
                  lineHeight={"19px"}
                  fontWeight={"600"}
                  color={"var(--color-header-employee)"}
                  mb={"24px"}
                >
                  Đợt mở bán: {name || ""}
                </Text>
              </Box>

              <Flex
                w={"full"}
                p={"0px 16px 0px 16px"}
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <Flex gap={"52px"} alignItems={"center"}>
                  <Text
                    fontSize={"16px"}
                    lineHeight={"19px"}
                    fontWeight={"600"}
                    color={"var(--color-header-employee)"}
                  >
                    Cơ chế tính toán hệ số K
                  </Text>

                  <Flex gap={"8px"}>
                    <Text
                      m="0"
                      fontSize={{ base: "14px" }}
                      fontWeight={400}
                      color={"var(--color-label-employee)"}
                    >
                      Tính hệ số k bằng cơ chế:
                    </Text>
                    <Switch
                      isChecked={checkedSwitch}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        resetAutoRecalcGuard();
                        setCheckedSwitch(checked);
                        setValue("statusKRate", checked ? 1 : 2);
                      }}
                    />
                  </Flex>

                  <Flex gap={"8px"}>
                    <Text
                      m="0"
                      fontSize={{ base: "14px" }}
                      fontWeight={400}
                      color={"var(--color-label-employee)"}
                    >
                      Tự động tính:
                    </Text>
                    <Switch
                      isChecked={isAutoCalculate}
                      isDisabled={!checkedSwitch}
                      onChange={(e) => {
                        setIsAutoCalculate(e.target.checked);
                      }}
                    />
                  </Flex>
                </Flex>

                <Button
                  w={"176px"}
                  h={"44px"}
                  display={"flex"}
                  alignItems={"center"}
                  p={"10px 16px 10px 12px"}
                  gap={"8px"}
                  backgroundImage="var(--color-button)"
                  _hover={{
                    filter: "brightness(90%)",
                  }}
                  type="submit"
                  name="action"
                  value="edit"
                >
                  <Text
                    fontSize={{ base: "15px" }}
                    fontWeight={600}
                    color="var(--color-main)"
                  >
                    Xác nhận
                  </Text>
                </Button>
              </Flex>

              {checkedSwitch ? (
                <Flex width={"100%"} height={"100%"}>
                  <TableContainer
                    w={"100%"}
                    maxH="calc(100vh - 350.5px)"
                    overflowY="auto"
                  >
                    <Table w={"100%"} variant="unstyled">
                      <Thead
                        position="sticky"
                        top="0"
                        zIndex="1"
                        w={"100%"}
                        h={"41px"}
                        color="white"
                        fontWeight="400"
                        backgroundColor="#F9FAFD"
                      >
                        <Tr h={{ base: "41px" }}>
                          {tableData.headers.map((e, index) => {
                            let width = "auto";
                            let textAlign = "auto";
                            if (e.key === "") {
                              width = "2%";
                              textAlign = "center";
                            } else if (e.key === "code") {
                              width = "5%";
                              textAlign = "start";
                            } else if (e.key === "name") {
                              width = "5%";
                              textAlign = "start";
                            } else if (e.key === "mechanismRate") {
                              width = "10%";
                              textAlign = "start";
                            } else if (e.key === "rate") {
                              width = "10%";
                              textAlign = "start";
                            } else if (e.key === "mechanismRateElite") {
                              width = "10%";
                              textAlign = "start";
                            } else {
                              width = "10%";
                              textAlign = "start";
                            }

                            return (
                              <Td
                                p={"16px 16px 8px 16px"}
                                key={index}
                                border={"none"}
                                color={"#51607B"}
                                fontSize={{ base: "14px" }}
                                w={width}
                              >
                                <Box textAlign={"center"} fontWeight={"bold"}>{e.label}</Box>
                              </Td>
                            );
                          })}
                        </Tr>
                      </Thead>

                      <Tbody w={"100%"} h={"100%"}>
                        {tableData.data?.length > 0 ? (
                          tableData.data.map((e, rowIndex) => {
                            const items = { ...e };
                            delete items.id;
                            delete items.isInput;
                            delete items.isInputNoRate;
                            const keyValues = Object.keys(items);

                            return (
                              <Tr key={rowIndex} h={"82px"}>
                                {keyValues.map((keyvalue, index) => {
                                  let width = "auto";
                                  let widthText = "100%";
                                  let textAlign = "auto";
                                  let whiteSpace = "inherit";

                                  if (keyvalue === "") {
                                    width = "2%";
                                    textAlign = "center";
                                  } else if (keyvalue === "code") {
                                    width = "5%";
                                    textAlign = "start";
                                  } else if (keyvalue === "name") {
                                    width = "5%";
                                    textAlign = "start";
                                  } else if (keyvalue === "mechanismRate") {
                                    width = "10%";
                                    textAlign = "start";
                                  } else if (keyvalue === "rate") {
                                    width = "10%";
                                    textAlign = "start";
                                  } else if (
                                    keyvalue === "mechanismRateElite"
                                  ) {
                                    width = "10%";
                                    textAlign = "start";
                                  } else {
                                    width = "10%";
                                    textAlign = "end";
                                    whiteSpace = "inherit";
                                  }

                                  return (
                                    <Td
                                      p={"16px 16px 8px 16px"}
                                      w={width}
                                      key={index}
                                    >
                                      <Box
                                        fontSize={{ base: "14px" }}
                                        lineHeight={"19.6px"}
                                        textAlign={"start"}
                                        fontWeight={500}
                                        color={"var(--color-info-employee)"}
                                        w={widthText}
                                        whiteSpace={whiteSpace}
                                        textOverflow="ellipsis"
                                        display="-webkit-box"
                                        sx={{
                                          WebkitLineClamp: "2",
                                          WebkitBoxOrient: "vertical",
                                        }}
                                      >
                                        {formatTableValue(
                                          e[keyvalue],
                                          keyvalue,
                                          isAutoCalculate &&
                                            (e?.code === "TOTAL" ||
                                              e?.code === "REMAIN"),
                                          handleChangeInput,
                                          e.id,
                                          null,
                                          e?.code,
                                        )}
                                      </Box>
                                    </Td>
                                  );
                                })}
                              </Tr>
                            );
                          })
                        ) : (
                          <Tr>
                            <Td
                              colSpan={tableData.headers.length}
                              textAlign="center"
                              padding={"40px 0"}
                            >
                              Không có dữ liệu
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </TableContainer>

                  <Box
                    w={"25%"}
                    borderLeft={
                      "1px solid var(--chakra-colors-chakra-border-color)"
                    }
                  >
                    <Box
                      p={"16px 16px 8px 16px"}
                      border={"none"}
                      color={"#293755"}
                      fontSize={{ base: "16px" }}
                      fontWeight={600}
                      w={"100%"}
                      textAlign={"left"}
                    >
                      <Flex
                        alignItems={"center"}
                        gap={"8px"}
                        justifyContent={"flex-end"}
                      >
                        <Text minW={"140px"}>Hệ số K: </Text>
                        <Input
                          w={"100%"}
                          step={"any"}
                          type="number"
                          value={Number(kRate)}
                          placeholder="Nhập số"
                          onChange={(e) => {
                            const newValueNumber = Number(e.target.value);
                            setKRate(newValueNumber);
                            setValue("kRate", newValueNumber);
                          }}
                        />
                      </Flex>
                    </Box>

                    <Box
                      p={"16px 16px 8px 16px"}
                      border={"none"}
                      color={"#293755"}
                      fontSize={{ base: "16px" }}
                      fontWeight={600}
                      w={"100%"}
                      textAlign={"left"}
                    >
                      <Flex
                        alignItems={"center"}
                        gap={"8px"}
                        justifyContent={"flex-end"}
                      >
                        <Text minW={"140px"}>Tỉ lệ phí dịch vụ mặc định: </Text>
                        <Input
                          w={"100%"}
                          step={"any"}
                          type="number"
                          value={Number(rateDefault)}
                          placeholder="Nhập số"
                          onChange={(e) => {
                            const newValueNumber = Number(e.target.value);
                            setValue("rateDefault", newValueNumber);
                            setRateDefault(newValueNumber);
                          }}
                        />
                      </Flex>
                    </Box>

                    <Box
                      p={"16px 16px 8px 16px"}
                      border={"none"}
                      color={"#293755"}
                      fontSize={{ base: "16px" }}
                      fontWeight={600}
                      w={"100%"}
                      textAlign={"left"}
                    >
                      <Flex
                        alignItems={"center"}
                        gap={"8px"}
                        justifyContent={"flex-end"}
                      >
                        <Text minW={"140px"}>Tỉ lệ phí dịch vụ:</Text>
                        <Input
                          w={"100%"}
                          step={"any"}
                          type="number"
                          value={Number(rate)}
                          placeholder="Nhập số"
                          onChange={(e) => {
                            const newValueNumber = Number(e.target.value);
                            setRate(newValueNumber);
                            setValue("rate", newValueNumber);
                          }}
                        />
                      </Flex>
                    </Box>

                    <Box
                      p={"16px 16px 8px 16px"}
                      border={"none"}
                      color={"#293755"}
                      fontSize={{ base: "16px" }}
                      fontWeight={600}
                      w={"100%"}
                      textAlign={"left"}
                    >
                      <Flex
                        alignItems={"center"}
                        gap={"8px"}
                        justifyContent={"flex-end"}
                      >
                        <Text minW={"140px"}>
                          Tỉ lệ phí dịch vụ Elite mặc định:
                        </Text>
                        <Input
                          w={"100%"}
                          step={"any"}
                          type="number"
                          value={Number(rateEliteDefault)}
                          placeholder="Nhập số"
                          onChange={(e) => {
                            const newValueNumber = Number(e.target.value);
                            setValue("rateEliteDefault", newValueNumber);
                            setRateEliteDefault(newValueNumber);
                          }}
                        />
                      </Flex>
                    </Box>

                    <Box
                      p={"16px 16px 8px 16px"}
                      border={"none"}
                      color={"#293755"}
                      fontSize={{ base: "16px" }}
                      fontWeight={600}
                      w={"100%"}
                      textAlign={"left"}
                    >
                      <Flex
                        alignItems={"center"}
                        gap={"8px"}
                        justifyContent={"flex-end"}
                      >
                        <Text minW={"140px"}>Tỉ lệ Elite</Text>
                        <Input
                          w={"100%"}
                          step={"any"}
                          type="number"
                          value={Number(rateElite)}
                          placeholder="Nhập số"
                          onChange={(e) => {
                            const newValueNumber = Number(e.target.value);
                            setRateElite(newValueNumber);
                            setValue("rateElite", newValueNumber);
                          }}
                        />
                      </Flex>
                    </Box>

                    <Box
                      p={"16px 16px 8px 16px"}
                      border={"none"}
                      color={"#293755"}
                      fontSize={{ base: "16px" }}
                      fontWeight={600}
                      w={"100%"}
                      textAlign={"left"}
                    >
                      <Flex
                        alignItems={"center"}
                        gap={"8px"}
                        justifyContent={"flex-end"}
                      >
                        <Text minW={"140px"}>
                          Tỉ lệ TN MSH (đi kèm hệ số K):
                        </Text>
                        <Input
                          w={"100%"}
                          step={"any"}
                          type="number"
                          value={Number(rateTN)}
                          placeholder="Nhập số"
                          onChange={(e) => {
                            const newValueNumber = Number(e.target.value);
                            setRateTN(newValueNumber);
                            setValue("rateTN", newValueNumber);
                          }}
                        />
                      </Flex>
                    </Box>

                    <Box
                      p={"16px 16px 8px 16px"}
                      border={"none"}
                      color={"#293755"}
                      fontSize={{ base: "16px" }}
                      fontWeight={600}
                      w={"100%"}
                      textAlign={"left"}
                    >
                      <Flex
                        alignItems={"center"}
                        gap={"8px"}
                        justifyContent={"flex-end"}
                      >
                        <Text minW={"140px"}>Tổng PDV + TN CVKD Elite:</Text>
                        <Input
                          w={"100%"}
                          step={"any"}
                          type="number"
                          value={Number(ratePDVAndTNCVKDElite)}
                          placeholder="Nhập số"
                          onChange={(e) => {
                            const newValueNumber = Number(e.target.value);
                            setRatePDVAndTNCVKDElite(newValueNumber);
                          }}
                        />
                      </Flex>
                    </Box>

                    <Box
                      p={"16px 16px 8px 16px"}
                      border={"none"}
                      color={"#293755"}
                      fontSize={{ base: "16px" }}
                      fontWeight={600}
                      w={"100%"}
                      textAlign={"left"}
                    >
                      <Flex
                        alignItems={"center"}
                        gap={"8px"}
                        justifyContent={"flex-end"}
                      >
                        <Text minW={"140px"}>
                          Tỉ lệ % phí dịch vụ nhận về: (Tỉ lệ GD)
                        </Text>
                        <Input
                          w={"100%"}
                          step={"any"}
                          type="number"
                          value={Number(transactionRate)}
                          placeholder="Nhập số"
                          onChange={(e) => {
                            const newValueNumber = Number(e.target.value);
                            setTransactionRate(newValueNumber);
                            setValue("transactionRate", newValueNumber);
                          }}
                        />
                      </Flex>
                      <Text
                        mt={"6px"}
                        color={"var(--color-secondary)"}
                        fontSize={"14px"}
                        fontWeight={400}
                        minW={"140px"}
                      >
                        (Yêu cầu nhập phần này)
                      </Text>
                    </Box>

                    <Box
                      p={"16px 16px 8px 16px"}
                      border={"none"}
                      color={"#293755"}
                      fontSize={{ base: "16px" }}
                      fontWeight={600}
                      w={"100%"}
                      textAlign={"left"}
                    >
                      <TextareaControl
                        type="textarea"
                        label="Thêm phản hồi"
                        placeholder="Nhập phản hồi"
                        errors={errors.note}
                        register={register("note")}
                        mwidth="100%"
                        height="150px"
                      />
                    </Box>
                  </Box>
                </Flex>
              ) : (
                <Box p={"24px 40px"}>
                  <Flex mt={"16px"} gap={"27px"}>
                    <InputControl
                      type="number"
                      label="Hệ số k"
                      placeholder="Ví dụ: 1.2 "
                      errors={errors.kRate}
                      register={register("kRate")}
                      mwidth={"100%"}
                      isRequired={true}
                    />
                  </Flex>

                  <Flex mt={"16px"} gap={"27px"}>
                    <InputControl
                      type="number"
                      label="Tỉ lệ % phí dịch vụ nhận về: (Tỉ lệ GD)"
                      placeholder="Ví dụ: 50.2 "
                      errors={errors.transactionRate}
                      register={register("transactionRate")}
                      mwidth={"100%"}
                      isRequired={true}
                    />
                  </Flex>

                  <Flex mt={"16px"} gap={"27px"}>
                    <InputControl
                      type="number"
                      label="Tỉ lệ phí dịch vụ mặc định"
                      placeholder="Ví dụ: 1.5 "
                      errors={errors.rateDefault}
                      register={register("rateDefault")}
                      mwidth={"100%"}
                      isRequired={true}
                    />
                  </Flex>

                  <Flex mt={"16px"} gap={"27px"}>
                    <InputControl
                      type="number"
                      label="Tỉ lệ phí dịch vụ"
                      placeholder="Ví dụ: 1.5 "
                      errors={errors.rate}
                      register={register("rate")}
                      mwidth={"100%"}
                      isRequired={true}
                    />
                  </Flex>

                  <Flex mt={"16px"} gap={"27px"}>
                    <InputControl
                      type="number"
                      label="Tỉ lệ phí dịch vụ Elite mặc định"
                      placeholder="Ví dụ: 0.2 "
                      errors={errors.rateEliteDefault}
                      register={register("rateEliteDefault")}
                      mwidth={"100%"}
                      isRequired={true}
                    />
                  </Flex>

                  <Flex mt={"16px"} gap={"27px"}>
                    <InputControl
                      type="number"
                      label={"Tỉ lệ Elite"}
                      placeholder="Ví dụ: 0.2 "
                      errors={errors.rateElite}
                      register={register("rateElite")}
                      mwidth={"100%"}
                      isRequired={true}
                    />
                  </Flex>

                  <Flex mt={"16px"} gap={"27px"}>
                    <InputControl
                      type="number"
                      label="Tỉ lệ TN MSH (đi kèm hệ số K)"
                      placeholder="Ví dụ: 0.6 "
                      errors={errors.rateTN}
                      register={register("rateTN")}
                      mwidth={"100%"}
                      isRequired={true}
                    />
                  </Flex>

                  <Flex mt={"16px"} gap={"27px"}>
                    <TextareaControl
                      type="textarea"
                      label="Thêm phản hồi"
                      placeholder="Nhập phản hồi"
                      errors={errors.note}
                      register={register("note")}
                      mwidth="100%"
                      height="150px"
                    />
                  </Flex>
                </Box>
              )}
            </Flex>
          </Flex>
        </form>
      </Box>
    </>
  );
};

export default TableUpdateProjectMechanism;
